import { initializeApp } from "firebase/app"
import "firebase/compat/auth"
import {
    GoogleAuthProvider,
    getAuth,
    signInWithPopup,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    sendEmailVerification,
    sendPasswordResetEmail,
    signOut,
    setPersistence,
    browserSessionPersistence
} from "firebase/auth"
import {
    getFirestore,
    query,
    getDocs,
    collection,
    where,
    addDoc
} from "firebase/firestore"
import dayjs from "dayjs"
import api from "./api_ui"
import { useEffect } from "react"

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
}

const app = initializeApp(firebaseConfig)
const auth = getAuth(app)
setPersistence(auth, browserSessionPersistence)
    .then(() => {
        return null
    })
    .catch((error) => {
        console.log("auth_error", error.code, error.message)
    })

const db = getFirestore(app)
const googleProvider = new GoogleAuthProvider()

const signInWithGoogle = async () => {
    try {
        const res = await signInWithPopup(auth, googleProvider)
        const user = res.user

        // Add user to database BEFORE any auth state changes propagate
        try {
            const userData = await api.add_user({
                id: user.uid,
                name: user.displayName,
                email: user.email,
                isVerified: user.emailVerified
            })
        } catch (error) {
            // If user already exists, that's fine - just log it
            if (error.error && error.error.includes("already exists")) {
                console.log("User already exists in database:", error.error)
            } else {
                console.log("HERE", error)
                // Don't throw here - user might still be able to proceed
            }
        }

        // Firestore operations (keeping existing logic)
        const q = query(
            collection(db, "research-users"),
            where("uid", "==", user.uid)
        )
        const docs = await getDocs(q)

        if (docs.docs.length === 0) {
            await addDoc(collection(db, "research-users"), {
                uid: user.uid,
                name: user.displayName,
                authProvider: "google",
                email: user.email,
                subscribed: true,
                timestamp: dayjs().format("DD/MMMM/YYYY HH:mm:ss")
            })
        } else {
            await addDoc(collection(db, "research-logs"), {
                uid: user.uid,
                name: user.displayName,
                authProvider: "google",
                email: user.email,
                timestamp: dayjs().format("DD/MMMM/YYYY HH:mm:ss")
            })
        }
    } catch (err) {
        console.error(err)
        alert("Kindly login using your Gmail account")
    }
}

const sendEmailVerificationFromFirebase = async () => {
    try {
        const user = auth.currentUser
        if (user && !user.emailVerified) {
            await sendEmailVerification(user)
            return {
                success: true,
                message: "Verification email sent! Please check your inbox."
            }
        }
        return {
            success: false,
            error: "No user found or email already verified"
        }
    } catch (err) {
        console.error(err)
        return {
            success: false,
            error: "Failed to send verification email. Please try again."
        }
    }
}

const logInWithEmailAndPassword = async (email, password) => {
    try {
        const res = await signInWithEmailAndPassword(auth, email, password)
        const user = res.user

        return {
            success: true,
            user,
            isVerified: user.emailVerified,
            message: user.emailVerified
                ? "Login successful"
                : "Please verify your email"
        }
    } catch (err) {
        console.error(err.code)
        let message = "Kindly enter correct Email and Password"

        if (err.code === "auth/user-not-found") {
            message = "No account found with this email address"
        } else if (err.code === "auth/wrong-password") {
            message = "Incorrect password"
        } else if (err.code === "auth/invalid-email") {
            message = "Please enter a valid email address"
        } else if (err.code === "auth/too-many-requests") {
            message = "Too many failed attempts. Please try again later"
        }

        return {
            success: false,
            error: message
        }
    }
}

const registerWithEmailAndPassword = async (name, email, password) => {
    try {
        const res = await createUserWithEmailAndPassword(auth, email, password)
        const user = res.user

        // Send verification email
        if (!user.emailVerified) {
            await sendEmailVerificationFromFirebase()
        }

        return {
            success: true,
            user,
            message:
                "Registration successful! Please check your email to verify your account."
        }
    } catch (err) {
        console.error(err.code)
        let message = "Registration failed. Please try again."

        if (err.code === "auth/email-already-in-use") {
            message =
                "This email is already in use. Please use a different email or try logging in."
        } else if (err.code === "auth/weak-password") {
            message = "Password should be at least 6 characters long."
        } else if (err.code === "auth/invalid-email") {
            message = "Please enter a valid email address."
        }

        return {
            success: false,
            error: message
        }
    }
}

const sendPasswordReset = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email)
        alert("Password reset link sent!")
    } catch (err) {
        console.error(err)
        // alert(err.message);
        alert("Kindly enter correct E-mail")
    }
}

const logout = (history) => {
    signOut(auth).then(() => {
        // Redirect or perform other actions after logout
        history("/login") // Replace "/login" with your desired redirect path
    })
}

const storeShareLogs = async (source) => {
    let user = auth.currentUser
    console.log(user, Object.keys(user))

    await addDoc(collection(db, "share-logs"), {
        email: user.email,
        name: user.displayName,
        source: source,
        timestamp: dayjs().format("DD/MMMM/YYYY HH:mm:ss")
    }).then((rese) => {
        return true
    })
}

const getCurrentUser = async () => {
    let user = auth.currentUser
    return user
}

export {
    auth,
    db,
    signInWithGoogle,
    logInWithEmailAndPassword,
    registerWithEmailAndPassword,
    sendPasswordReset,
    logout,
    storeShareLogs,
    getCurrentUser,
    sendEmailVerificationFromFirebase
}
